import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { translate } from '../../../base/i18n/functions';
import Button from '../../../base/ui/components/web/Button';
import { BUTTON_TYPES } from '../../../base/ui/constants.any';
import { isInterpreter } from '../../../base/user-role/functions';
import { acceptInterpreterRequest, declineInterpreterRequest } from '../../actions';
import { queueService } from '../../InterpreterQueueService';
import { startInterpreterOfferAlert } from '../../interpreterOfferAlert.web';
import { offerTiming } from '../../offerTiming';
const useStyles = makeStyles()(() => ({
    container: {
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        width: 'min(400px, calc(100vw - 40px))',
        maxHeight: 'calc(100vh - 40px)',
        overflowY: 'auto'
    },
    notification: {
        backgroundColor: 'rgba(26, 35, 50, 0.95)',
        border: '2px solid #4a9eff',
        borderRadius: '12px',
        boxSizing: 'border-box',
        padding: '16px 20px',
        width: '100%',
        overflowWrap: 'anywhere',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        animation: 'slideInRight 0.3s ease-out',
        '@keyframes slideInRight': {
            from: {
                opacity: 0,
                transform: 'translateX(50px)'
            },
            to: {
                opacity: 1,
                transform: 'translateX(0)'
            }
        },
        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none'
        }
    },
    inboundNotification: {
        backgroundColor: 'rgba(48, 36, 18, 0.97)',
        borderColor: '#f59e0b',
        boxShadow: '0 8px 32px rgba(245, 158, 11, 0.24)'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px'
    },
    title: {
        color: '#ffffff',
        fontSize: '17px',
        fontWeight: 'bold',
        margin: 0
    },
    content: {
        color: '#e0e0e0',
        fontSize: '13px',
        marginBottom: '16px',
        lineHeight: '1.5'
    },
    clientName: {
        color: '#75baff',
        fontWeight: 'bold'
    },
    language: {
        color: '#ffd54f',
        fontWeight: 'bold'
    },
    inboundRoute: {
        color: '#fde7b0',
        fontSize: '16px',
        fontWeight: 700,
        marginBottom: '6px'
    },
    notes: {
        backgroundColor: 'rgba(255, 255, 255, 0.07)',
        borderRadius: '6px',
        marginTop: '8px',
        padding: '8px'
    },
    timing: {
        fontWeight: 700,
        marginTop: '8px'
    },
    expired: {
        color: '#ffb4ab'
    },
    error: {
        backgroundColor: 'rgba(220, 53, 69, 0.18)',
        border: '1px solid #ff8a93',
        borderRadius: '6px',
        color: '#ffffff',
        marginBottom: '12px',
        padding: '10px'
    },
    actions: {
        display: 'flex',
        gap: '10px'
    },
    acceptButton: {
        flex: 1,
        minHeight: '44px',
        backgroundColor: '#28a745',
        '&:hover': {
            backgroundColor: '#218838'
        }
    },
    declineButton: {
        flex: 1,
        minHeight: '44px',
        backgroundColor: '#dc3545',
        '&:hover': {
            backgroundColor: '#c82333'
        }
    },
    ringingIndicator: {
        position: 'absolute',
        top: '-5px',
        right: '-5px',
        width: '12px',
        height: '12px',
        backgroundColor: '#ff7849',
        borderRadius: '50%',
        animation: 'pulse 1s ease-in-out infinite',
        '@keyframes pulse': {
            '0%, 100%': {
                opacity: 1,
                transform: 'scale(1)'
            },
            '50%': {
                opacity: 0.5,
                transform: 'scale(1.2)'
            }
        },
        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none'
        }
    },
    badge: {
        position: 'relative'
    }
}));
/**
 * In-meeting request notification for interpreters.
 * Shows incoming interpreter requests while the interpreter is already in a meeting.
 */
const InMeetingRequestNotification = ({ _pendingRequests, dispatch, t }) => {
    const { classes } = useStyles();
    const [actionError, setActionError] = useState(null);
    const [actionPending, setActionPending] = useState(null);
    const [now, setNow] = useState(Date.now());
    const attentionRequest = _pendingRequests[0];
    useEffect(() => {
        if (!isInterpreter() || !attentionRequest) {
            return undefined;
        }
        return startInterpreterOfferAlert(attentionRequest);
    }, [attentionRequest]);
    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, []);
    useEffect(() => {
        if (actionPending && !_pendingRequests.some(request => request.id === actionPending.requestId)) {
            setActionPending(null);
            setActionError(null);
        }
    }, [_pendingRequests, actionPending]);
    useEffect(() => {
        if (!actionPending) {
            return undefined;
        }
        const handleError = (data) => {
            if (data.operation === `${actionPending.kind}_request`
                && data.requestId === actionPending.requestId) {
                setActionPending(null);
                setActionError({
                    message: data.message || 'The request action was not confirmed. Please try again.',
                    requestId: actionPending.requestId
                });
            }
        };
        const timer = window.setTimeout(() => {
            setActionPending(null);
            setActionError({
                message: 'No confirmation was received. Check your connection, then try again if the offer is still available.',
                requestId: actionPending.requestId
            });
        }, 8000);
        queueService.on('error', handleError);
        return () => {
            window.clearTimeout(timer);
            queueService.off('error', handleError);
        };
    }, [actionPending]);
    if (!isInterpreter() || _pendingRequests.length === 0) {
        return null;
    }
    const beginAction = (request, kind) => {
        if (actionPending || offerTiming(request).expired) {
            return;
        }
        setActionError(null);
        setActionPending({ kind, requestId: request.id });
        dispatch(kind === 'accept'
            ? acceptInterpreterRequest(request.id)
            : declineInterpreterRequest(request.id));
    };
    return (React.createElement("div", { className: classes.container }, _pendingRequests.map(request => {
        const isInboundHearing = request.requestKind === 'inbound_hearing';
        const callerLast4 = request.targetPhoneLast4
            || String(request.targetPhone || '').replace(/\D/g, '').slice(-4)
            || 'unknown';
        const timing = offerTiming(request, now);
        const pendingKind = actionPending?.requestId === request.id ? actionPending.kind : null;
        const error = actionError?.requestId === request.id ? actionError.message : '';
        const identifier = request.id.replace(/[^A-Za-z0-9_-]/g, '-');
        const titleId = `meeting-request-${identifier}-title`;
        const contentId = `meeting-request-${identifier}-content`;
        return (React.createElement("section", { "aria-busy": Boolean(pendingKind), "aria-describedby": contentId, "aria-labelledby": titleId, className: `${classes.notification} ${isInboundHearing ? classes.inboundNotification : ''}`, key: request.id, role: 'region' },
            React.createElement("div", { className: classes.header },
                React.createElement("div", { className: classes.badge },
                    React.createElement("div", { "aria-hidden": 'true', className: classes.ringingIndicator }),
                    React.createElement("h2", { className: classes.title, id: titleId }, isInboundHearing ? 'Inbound hearing call' : t('vrs.newInterpreterRequest')))),
            React.createElement("div", { className: classes.content, id: contentId },
                isInboundHearing ? (React.createElement(React.Fragment, null,
                    React.createElement("p", { className: classes.inboundRoute },
                        "Hearing caller ending ",
                        callerLast4,
                        " \u2192 ",
                        request.clientName),
                    React.createElement("p", null,
                        "Answer the caller first, then ring ",
                        request.clientName,
                        "."))) : (React.createElement("p", null,
                    t('vrs.clientWaiting'),
                    ": ",
                    React.createElement("span", { className: classes.clientName }, request.clientName))),
                React.createElement("p", null,
                    t('vrs.language'),
                    ": ",
                    React.createElement("span", { className: classes.language }, request.language)),
                request.demoMode && (React.createElement("p", { className: classes.notes, role: 'note' }, "Demo call \u2014 not payable (Demo account, mock-billed).")),
                request.instructions && (React.createElement("p", { className: classes.notes },
                    isInboundHearing ? 'Notes' : 'Instructions',
                    ": ",
                    request.instructions)),
                React.createElement("p", { className: `${classes.timing} ${timing.expired ? classes.expired : ''}`, role: timing.expired ? 'status' : undefined }, timing.label)),
            error && React.createElement("div", { className: classes.error, role: 'alert' }, error),
            React.createElement("div", { className: classes.actions },
                React.createElement(Button, { accessibilityLabel: isInboundHearing
                        ? 'Accept and answer inbound hearing call'
                        : `Accept request from ${request.clientName}`, className: classes.acceptButton, disabled: timing.expired || Boolean(actionPending), onClick: () => beginAction(request, 'accept'), label: pendingKind === 'accept'
                        ? 'Accepting…'
                        : timing.expired ? 'Expired' : isInboundHearing ? 'Accept & Answer' : t('vrs.accept'), type: BUTTON_TYPES.PRIMARY }),
                React.createElement(Button, { accessibilityLabel: `Decline request from ${request.clientName}`, className: classes.declineButton, disabled: timing.expired || Boolean(actionPending), onClick: () => beginAction(request, 'decline'), label: pendingKind === 'decline' ? 'Declining…' : t('vrs.decline'), type: BUTTON_TYPES.SECONDARY }))));
    })));
};
function _mapStateToProps(state) {
    return {
        _pendingRequests: state['features/interpreter-queue']?.pendingRequests || []
    };
}
export { InMeetingRequestNotification };
export default translate(connect(_mapStateToProps)(InMeetingRequestNotification));
